import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { ChevronLeft, ChevronRight, Circle, CheckCircle2 } from "lucide-react";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const PRIORITY_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-slate-400" };

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function monthGrid(year, monthIdx) {
  // 6-row grid starting from Monday of the week containing the 1st.
  const first = new Date(year, monthIdx, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(year, monthIdx, 1 - dayOfWeek);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function TaskCalendar({ user, filterRole }) {
  const [tasks, setTasks] = useState([]);
  const [cursor, setCursor] = useState(() => new Date());

  const load = useCallback(async () => {
    const { data } = await api.get("/tasks");
    setTasks(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener("aii:task_update", onUpdate);
    return () => window.removeEventListener("aii:task_update", onUpdate);
  }, [load]);

  const y = cursor.getFullYear();
  const m = cursor.getMonth();

  const filtered = useMemo(() => {
    return tasks.filter(t => (!filterRole || t.assignee_role === filterRole) && t.due_date);
  }, [tasks, filterRole]);

  const byDate = useMemo(() => {
    const map = {};
    for (const t of filtered) {
      const key = t.due_date.slice(0, 10);
      (map[key] = map[key] || []).push(t);
    }
    return map;
  }, [filtered]);

  const cells = useMemo(() => monthGrid(y, m), [y, m]);
  const todayISO = toISO(new Date());
  const noDueCount = tasks.filter(t => (!filterRole || t.assignee_role === filterRole) && !t.due_date).length;

  const jump = (offset) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + offset, 1));

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { done: !t.done });
    await load();
  };

  return (
    <div className="aii-card p-4" data-testid="task-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => jump(-1)} className="p-1.5 rounded-md hover:bg-[color:var(--aii-green-tint)]"><ChevronLeft size={16} /></button>
          <div className="font-semibold text-lg">{MONTHS[m]} {y}</div>
          <button data-testid="cal-next" onClick={() => jump(1)} className="p-1.5 rounded-md hover:bg-[color:var(--aii-green-tint)]"><ChevronRight size={16} /></button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)]">Today</button>
        </div>
        <div className="text-xs text-[color:var(--aii-muted)]">
          {filtered.length} task{filtered.length !== 1 ? "s" : ""} scheduled · {noDueCount} without due date
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-[color:var(--aii-border)] rounded-md overflow-hidden border border-[color:var(--aii-border)]">
        {DOW.map(d => (
          <div key={d} className="bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] text-xs font-semibold uppercase tracking-wider p-2 text-center">{d}</div>
        ))}
        {cells.map((d, idx) => {
          const iso = toISO(d);
          const inMonth = d.getMonth() === m;
          const dayTasks = byDate[iso] || [];
          const isToday = iso === todayISO;
          return (
            <div key={iso} data-testid={`cal-day-${iso}`}
              className={`bg-white min-h-[92px] p-1.5 flex flex-col gap-0.5 ${inMonth ? "" : "opacity-40"}`}>
              <div className={`text-[11px] font-semibold ${isToday ? "bg-brand text-white rounded-full w-5 h-5 flex items-center justify-center" : "text-[color:var(--aii-text-2)]"}`}>{d.getDate()}</div>
              {dayTasks.slice(0, 3).map(t => (
                <button key={t.id} data-testid={`cal-task-${t.id}`} onClick={() => toggle(t)}
                  title={`${t.title} — ${t.assignee_role}`}
                  className={`text-[10px] leading-tight px-1.5 py-1 rounded flex items-center gap-1 text-left ${t.done ? "bg-[color:var(--aii-bg)] text-[color:var(--aii-muted)] line-through" : "bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] hover:bg-[color:var(--aii-green-tint-dark,#c9e2d3)]"}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[t.priority] || "bg-slate-400"} shrink-0`} />
                  {t.done ? <CheckCircle2 size={9} className="shrink-0" /> : <Circle size={9} className="shrink-0" />}
                  <span className="truncate">{t.title}</span>
                </button>
              ))}
              {dayTasks.length > 3 && (
                <div className="text-[10px] text-[color:var(--aii-muted)] mt-auto pl-1">+{dayTasks.length - 3} more</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 text-[11px] text-[color:var(--aii-muted)]">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> High</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Medium</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Low</span>
        <span className="ml-auto">Click a task to toggle done</span>
      </div>
    </div>
  );
}
